// frontend/packages/business-console/src/pages/Crm.jsx
//
// CRM customer list. Mobile-first: single-column stack on phones, light
// summary at the top, sticky filter bar with stage tabs and a free-text
// search. Multi-business owners get an "Across my businesses" toggle that
// flips the API call to query the union of their authorized tenants.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { getInitials } from '@upryzze/ui';

import { BusinessContext } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import { listCustomers } from '../services/crmService';
import AddCustomerDialog from '../components/crm/AddCustomerDialog';
import { fetchCrmPulse, fetchPipelinePulse } from '../services/pulseService';
import { demoCrm, demoPipeline, demoCustomers } from './Pulse/demoData';
import {
  useTokens,
  Surface,
  StatusPill,
  PageHeader,
  GradientHeroCard,
} from '../components/primitives';

// Table column template, shared by the header and rows (design grid).
const CRM_GRID = '1fr 116px 120px 150px 40px';

// Segment tabs per the design: All / Leads / Active / Repeat / Past. The
// keys map to the real Customer.stage values the list endpoint filters on.
const STAGES = [
  { key: 'all', label: 'All', filter: {} },
  { key: 'lead', label: 'Leads', filter: { stage: 'lead' } },
  { key: 'customer', label: 'Active', filter: { stage: 'customer' } },
  { key: 'repeat', label: 'Repeat', filter: { stage: 'repeat' } },
  { key: 'lost', label: 'Past', filter: { stage: 'lost' } },
];

// Stage -> display label for the table pill (design copy).
const STAGE_LABEL = {
  lead: 'New lead',
  contacted: 'Contacted',
  qualified: 'Qualified',
  customer: 'Active',
  repeat: 'Repeat',
  lost: 'Lost',
};

const fmtUsdCents = (cents) =>
  cents > 0 ? `$${Math.round(cents / 100).toLocaleString()}` : '—';

// Stage -> shared StatusPill kind (semantic color).
const STAGE_KIND = {
  lead: 'info',
  contacted: 'warning',
  qualified: 'ai',
  customer: 'success',
  repeat: 'success',
  lost: 'neutral',
};

const formatRelativeDate = (input) => {
  if (!input) return '—';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '—';
  const days = Math.round(
    (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (days < 1) return 'today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return date.toLocaleDateString();
};

export default function Crm() {
  const { business } = useContext(BusinessContext);
  const { user } = useAuth();
  const t = useTokens();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const basePath = useMemo(
    () => (business?.slug ? `/business/${business.slug}` : ''),
    [business?.slug]
  );

  // Multi-business owners (admin/manager on >1 business) see the toggle.
  // Everyone else just sees their single tenant.
  const multiBusiness = useMemo(() => {
    if (!user?.businessAccess || !Array.isArray(user.businessAccess))
      return false;
    const allowed = new Set(['businessadmin', 'manager', 'employee']);
    const count = user.businessAccess.filter((e) =>
      allowed.has(String(e?.role || '').toLowerCase())
    ).length;
    return count > 1;
  }, [user]);

  // ?stage=X / ?q=Y hydrate filter chips so Pulse deep-links land
  // pre-filtered (and the URL is shareable / back-button-friendly).
  const [searchParams, setSearchParams] = useSearchParams();
  // Demo flag: `?demo=1` or localStorage `pulse.demo` = "1" (same toggle the
  // Pulse + Analytics pages use). Fills the table + stat cards from fixtures.
  const isDemo = useMemo(() => {
    if (searchParams.get('demo') === '1') return true;
    try {
      return localStorage.getItem('pulse.demo') === '1';
    } catch {
      return false;
    }
  }, [searchParams]);
  const allowedStages = useMemo(() => new Set(STAGES.map((s) => s.key)), []);
  const urlStage = searchParams.get('stage');
  const urlQ = searchParams.get('q') || '';
  const [stageKey, setStageKey] = useState(
    urlStage && allowedStages.has(urlStage) ? urlStage : 'all'
  );
  const [search, setSearch] = useState(urlQ);
  const [debouncedSearch, setDebouncedSearch] = useState(urlQ);
  const [acrossBusinesses, setAcrossBusinesses] = useState(false);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (stageKey === 'all') next.delete('stage');
    else next.set('stage', stageKey);
    if (!debouncedSearch) next.delete('q');
    else next.set('q', debouncedSearch);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [stageKey, debouncedSearch, searchParams, setSearchParams]);

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const buildParams = useCallback(() => {
    const stage = STAGES.find((t) => t.key === stageKey) || STAGES[0];
    const params = { ...stage.filter };
    if (debouncedSearch) params.q = debouncedSearch;
    if (acrossBusinesses) params.acrossMyBusinesses = true;
    return params;
  }, [stageKey, debouncedSearch, acrossBusinesses]);

  const loadFirstPage = useCallback(async () => {
    if (isDemo) {
      const stage = STAGES.find((s) => s.key === stageKey) || STAGES[0];
      const wanted = stage.filter.stage;
      const rows = wanted
        ? demoCustomers.filter((c) => c.stage === wanted)
        : demoCustomers;
      setItems(rows);
      setHasMore(false);
      setCursor(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await listCustomers(buildParams());
      setItems(result.items || []);
      setHasMore(Boolean(result.hasMore));
      setCursor(result.nextCursor || null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load customers'
      );
    } finally {
      setLoading(false);
    }
  }, [buildParams, isDemo, stageKey]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !cursor) return;
    setLoadingMore(true);
    try {
      const result = await listCustomers({ ...buildParams(), before: cursor });
      setItems((prev) => [...prev, ...(result.items || [])]);
      setHasMore(Boolean(result.hasMore));
      setCursor(result.nextCursor || null);
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || 'Failed to load more'
      );
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, cursor, buildParams]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  // Pipeline stat cards (redesign): stage counts from the CRM pulse +
  // quote pipeline from the pipeline pulse. Non-blocking; the table renders
  // regardless. Skipped in the across-businesses view (no single tenant).
  const [pulse, setPulse] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (isDemo) {
      setPulse(demoCrm);
      setPipeline(demoPipeline);
      return undefined;
    }
    if (acrossBusinesses) {
      setPulse(null);
      setPipeline(null);
      return undefined;
    }
    fetchCrmPulse()
      .then((d) => {
        if (!cancelled) setPulse(d);
      })
      .catch(() => {});
    fetchPipelinePulse()
      .then((d) => {
        if (!cancelled) setPipeline(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [acrossBusinesses, isDemo]);

  const byStage = useMemo(() => {
    const m = {};
    for (const row of pulse?.stageBreakdown || []) m[row.stage] = row.count;
    return m;
  }, [pulse]);

  const statCards = useMemo(() => {
    if (!pulse && !pipeline) return [];
    return [
      { label: 'New leads', value: byStage.lead || 0, bar: t.blue },
      { label: 'Quoted', value: pipeline?.sentCount || 0, bar: t.amber },
      { label: 'Active', value: byStage.customer || 0, bar: t.green },
      { label: 'Repeat', value: byStage.repeat || 0, bar: t.purple },
      {
        label: 'Win rate',
        value: `${pipeline?.conversionPct ?? 0}%`,
        sub: 'quotes accepted',
        gradient: true,
      },
    ];
  }, [pulse, pipeline, byStage, t]);

  return (
    <Box>
      <PageHeader
        title="CRM"
        sx={{ mb: 1.5 }}
        meta={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 13, color: t.muted, fontWeight: 600 }}>
              {acrossBusinesses
                ? 'Customers across all your businesses'
                : 'Your customers and leads'}
            </Typography>
            {isDemo ? (
              <StatusPill kind="info" size="sm" label="Demo data" />
            ) : null}
          </Stack>
        }
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<PersonAddAlt1OutlinedIcon />}
              onClick={() => setAddOpen(true)}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Add customer
            </Button>
            <Button
              startIcon={<RefreshOutlinedIcon />}
              onClick={loadFirstPage}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Stack>
        }
      />

      <AddCustomerDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(c) => {
          setAddOpen(false);
          // Land on the new customer so "New estimate" is one click away.
          // Fall back to refreshing the list if we somehow lack an id.
          if (c?._id && basePath) {
            navigate(`${basePath}/crm/${c._id}`);
          } else {
            loadFirstPage();
          }
        }}
      />

      {statCards.length ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
            gap: 1.25,
            mb: 1.5,
          }}
        >
          {statCards.map((c) => (
            <CrmStatCard key={c.label} card={c} t={t} />
          ))}
        </Box>
      ) : null}

      {multiBusiness && (
        <FormControlLabel
          control={
            <Switch
              checked={acrossBusinesses}
              onChange={(e) => setAcrossBusinesses(e.target.checked)}
              size="small"
            />
          }
          label={
            <Box
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <GroupOutlinedIcon fontSize="small" />
              <Typography variant="body2">Across my businesses</Typography>
            </Box>
          }
          sx={{ mb: 1 }}
        />
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{
          mb: 1.5,
          position: 'sticky',
          top: { xs: 56, md: 64 },
          zIndex: 1,
          backgroundColor: 'background.default',
          py: 1,
        }}
      >
        <Tabs
          value={stageKey}
          onChange={(_, v) => setStageKey(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none' },
          }}
        >
          {STAGES.map((s) => (
            <Tab key={s.key} value={s.key} label={s.label} />
          ))}
        </Tabs>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          placeholder="Search name, email, phone…"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            py: 8,
            textAlign: 'center',
            color: 'text.secondary',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <GroupOutlinedIcon sx={{ fontSize: 48, opacity: 0.5 }} />
          <Typography variant="subtitle1">No customers</Typography>
          <Typography variant="body2">
            {debouncedSearch
              ? 'Try a different search.'
              : 'Customers from your inbox land here once you add them to the CRM.'}
          </Typography>
        </Box>
      ) : (
        <Surface radius="14px" sx={{ overflow: 'hidden', p: 0 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 700 }}>
              {/* header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: CRM_GRID,
                  gap: 1.5,
                  alignItems: 'center',
                  px: 2.25,
                  py: 1.25,
                  borderBottom: `1px solid ${t.hairline}`,
                  bgcolor: t.tableHead,
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: '.5px',
                  textTransform: 'uppercase',
                  color: t.faint,
                }}
              >
                <Box>Customer</Box>
                <Box>Stage</Box>
                <Box sx={{ textAlign: 'right' }}>Lifetime value</Box>
                <Box>Last touch</Box>
                <Box />
              </Box>

              {items.map((c) => {
                const name =
                  `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
                  c.email ||
                  'Customer';
                return (
                  <Box
                    key={c._id}
                    component={RouterLink}
                    to={`${basePath}/crm/${c._id}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: CRM_GRID,
                      gap: 1.5,
                      alignItems: 'center',
                      textDecoration: 'none',
                      color: 'inherit',
                      px: 2.25,
                      py: 1.5,
                      borderBottom: `1px solid ${t.hairline}`,
                      '&:last-of-type': { borderBottom: 0 },
                      '&:hover': { backgroundColor: t.subtle },
                    }}
                  >
                    {/* Customer */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: t.blueBg,
                          color: t.blue,
                          fontSize: 12.5,
                          fontWeight: 800,
                        }}
                      >
                        {getInitials(name) || '?'}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: 13.5,
                            fontWeight: 800,
                            color: t.text,
                          }}
                        >
                          {name}
                        </Typography>
                        <Typography
                          noWrap
                          sx={{ fontSize: 11.5, color: t.faint }}
                        >
                          {c.email || c.phone || '—'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Stage */}
                    <Box>
                      {c.stage ? (
                        <StatusPill
                          size="sm"
                          kind={STAGE_KIND[c.stage] || 'neutral'}
                          label={STAGE_LABEL[c.stage] || c.stage}
                        />
                      ) : null}
                    </Box>

                    {/* Lifetime value */}
                    <Typography
                      sx={{
                        textAlign: 'right',
                        fontSize: 13.5,
                        fontWeight: 800,
                        color: c.lifetimeValueCents > 0 ? t.text : t.faint,
                      }}
                    >
                      {fmtUsdCents(c.lifetimeValueCents || 0)}
                    </Typography>

                    {/* Last touch */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{ fontSize: 12, color: t.textSecondary }}
                      >
                        {formatRelativeDate(c.lastContactedAt)}
                      </Typography>
                      {c.personId ? (
                        <LinkOutlinedIcon
                          sx={{ fontSize: 14, color: t.faint }}
                        />
                      ) : null}
                    </Box>

                    <Typography
                      sx={{ textAlign: 'center', color: t.faint, fontSize: 17 }}
                    >
                      &#8942;
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                sx={{ textTransform: 'none' }}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </Box>
          )}
        </Surface>
      )}
    </Box>
  );
}

// One pipeline stat card. The last card opts into the brand gradient (the
// "value" tile in the mock); the rest carry a colored stage bar.
function CrmStatCard({ card, t }) {
  if (card.gradient) {
    return (
      <GradientHeroCard radius="12px" sx={{ p: '11px 14px' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>
          {card.label}
        </Typography>
        <Typography
          sx={{
            fontSize: 21,
            fontWeight: 900,
            letterSpacing: '-0.5px',
            mt: '3px',
          }}
        >
          {card.value}
        </Typography>
        {card.sub ? (
          <Typography
            sx={{ fontSize: 10.5, fontWeight: 600, opacity: 0.85, mt: '9px' }}
          >
            {card.sub}
          </Typography>
        ) : null}
      </GradientHeroCard>
    );
  }
  return (
    <Surface radius="12px" sx={{ p: '11px 14px' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.muted }}>
        {card.label}
      </Typography>
      <Typography
        sx={{
          fontSize: 21,
          fontWeight: 900,
          letterSpacing: '-0.5px',
          mt: '3px',
          color: t.text,
        }}
      >
        {card.value}
      </Typography>
      <Box sx={{ height: 3, borderRadius: 3, bgcolor: card.bar, mt: '9px' }} />
    </Surface>
  );
}
